import { useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";

import { toastMessage } from "../../utils";
import { restApi } from "../../provider/restApi";
import { useGlobalContext } from "../../provider";
import { TextField } from "../../components/inputs/textfield";
import { FirstOutlineButton } from "../../components/buttons/buttons";
import { apiNotification } from "../../services/apiNotification";

interface StateObject {
	enterCode: boolean
	verifyCode: number | null
	errorVerifyCode: boolean
}

const initState: StateObject = {
	enterCode: false,
	verifyCode: null,
	errorVerifyCode: false
}

const Verify = () => {
	const [verifyCode, setVerifyCode] = useState("");
	const [state, setState] = useState<StateObject>(initState);
	const [globalState, { dispatch }]: GlobalContextType = useGlobalContext();

	const mailVerified = useMemo(() => {
		return globalState.userData.mailVerify === "Verified"
	}, [globalState.userData])

	const onChangeState = (updateData: Partial<StateObject>) => {
		setState({ ...state, ...updateData });
	}

	const onChangeInputValue = (key: string) => (e: any) => {
		const { name, value } = e.target;
		const code = value.toString().slice(0, 6)
		onChangeState({ [name]: Number(code) || null, [key]: false });
	}

	const checkValidate = (code: number): boolean => {
		const codeLength = code.toString().length;
		if (codeLength !== 6) return false;

		return true;
	}

	const requestVerifyCode = async () => {
		try {
			dispatch({ loading: true });

			const resData = await restApi.requestVerifyCode();
			setVerifyCode(resData.verifyCode)

			dispatch({ loading: false });
			onChangeState({ enterCode: true });
		} catch (err: any) {
			dispatch({ loading: false });
			apiNotification(err, "Request Verify Code failed!");
		}
	}

	const onVerifyEmail = async () => {
		try {
			if (mailVerified) return;
			const verifyCode = Number(state.verifyCode);
			const validate = checkValidate(verifyCode);

			if (!validate) {
				onChangeState({ errorVerifyCode: true });
				return false;
			}

			dispatch({ loading: true });
			const resData = await restApi.settingMailVerify({
				verifyCode: verifyCode.toString()
			})

			setVerifyCode("")
			onChangeState({ enterCode: false });
			dispatch({ loading: false, userData: resData })
			toastMessage("success", "Mail Verify successed!");
		} catch (error) {
			setVerifyCode("")
			dispatch({ loading: false, })
			apiNotification(error, "Mail Verify failed!");
		}
	}

	return (
		<VerifyWrapper>
			<Typography variant="h3">
				Mail Verifycation
			</Typography>

			<Typography variant="body1" component="h6">
				Mail Verification on our  platform ensures a secure and seamless user experience by verifying email addresses during registration, preventing unauthorized access, and enhancing communication reliability for important course updates and notifications.
			</Typography>

			<Typography variant="body2">{verifyCode}</Typography>

			<MailVerifyForm>
				<TextField fullWidth
					name="verifyCode"
					type="number" sx={{ my: 2 }}
					placeholder="Enter you verification code"
					helperText={!state.enterCode ? "" : "Enter your 6-digit verification code"}
					onChange={onChangeInputValue("errorVerifyCode")}
					error={state.errorVerifyCode}
					value={state.verifyCode}
					disabled={!state.enterCode}
				/>

				{!state.enterCode && (
					<FirstOutlineButton disabled={mailVerified} onClick={requestVerifyCode}>
						<Typography variant="body1">
							{mailVerified ? "Verified" : "Verify"}
						</Typography>
					</FirstOutlineButton>
				)}

				{state.enterCode && (
					<FirstOutlineButton disabled={mailVerified} onClick={onVerifyEmail}>
						<Typography variant="body1">
							{mailVerified ? "Verified" : "Submit Code"}
						</Typography>
					</FirstOutlineButton>
				)}
			</MailVerifyForm>
		</VerifyWrapper>
	)
}

const VerifyWrapper = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(2),

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(1),
	}
}))

const MailVerifyForm = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "start",
	gap: theme.spacing(2),

	".MuiFormControl-root": {
		margin: 0,
	},

	button: {
		minWidth: 150,
	},
}))

export { Verify }